import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  Checkbox,
  Paper,
  Avatar,
  Box,
} from "@mui/material";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { OrderDetail } from "../../types";
import { ArrivedWarehousePayload } from "./types";
import { useTranslation } from "../../../../hooks/useTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  order: OrderDetail;
  onSubmit: (_data: ArrivedWarehousePayload[]) => void;
  loading?: boolean;
};

export const ArrivedToWarehouseModal = ({
  open,
  onClose,
  order,
  onSubmit,
  loading = false,
}: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.arrivedToWarehouse",
  );

  /* =========================================================
     STATE
  ========================================================= */

  const [allArrived, setAllArrived] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  /* =========================================================
     HELPERS
  ========================================================= */

  const clampQuantity = useCallback(
    (value: number, min: number, max: number) => {
      if (Number.isNaN(value)) return min;
      return Math.min(Math.max(value, min), max);
    },
    [],
  );

  /**
   * ✅ NEW RULE
   * maxQty = item.quantity - (item.quantityShipped + item.receivedQuantity)
   * negative -> 0
   */
  const getMaxQty = useCallback((item: OrderDetail["orderItems"][number]) => {
    const remaining =
      item.quantity - (item.quantityShipped + item.receivedQuantity);
    return Math.max(remaining, 0);
  }, []);

  /* =========================================================
     INIT
  ========================================================= */

  useEffect(() => {
    if (!open) return;

    const nextQuantities: Record<number, number> = {};

    for (const item of order.orderItems) {
      const maxQty = getMaxQty(item);
      nextQuantities[item.id] = maxQty; // default = max
    }

    setAllArrived(true);
    setQuantities(nextQuantities);
  }, [open, order.orderItems, getMaxQty]);

  /* =========================================================
     DERIVED STATE
  ========================================================= */

  const isDirty = useMemo(() => {
    if (allArrived) return true;
    return Object.values(quantities).some((qty) => qty > 0);
  }, [allArrived, quantities]);

  /* =========================================================
     HANDLERS
  ========================================================= */

  const handleAllArrivedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAllArrived(e.target.checked);
    },
    [],
  );

  const handleQuantityChange = useCallback(
    (itemId: number, rawValue: number, maxQty: number) => {
      const clamped = clampQuantity(rawValue, 0, maxQty);

      setQuantities((prev) => {
        if (prev[itemId] === clamped) return prev;
        return {
          ...prev,
          [itemId]: clamped,
        };
      });
    },
    [clampQuantity],
  );

  const handleSubmit = useCallback(() => {
    const payload: ArrivedWarehousePayload[] = [];

    for (const item of order.orderItems) {
      const maxQty = getMaxQty(item);
      const rawQty = quantities[item.id] ?? 0;
      const qty = clampQuantity(rawQty, 0, maxQty); // ✅ double safety

      if (qty > 0) {
        payload.push({
          orderItemId: item.id,
          quantity: qty,
        });
      }
    }

    onSubmit(payload);
  }, [order.orderItems, quantities, onSubmit, clampQuantity, getMaxQty]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate("title")}</DialogTitle>

      <DialogContent dividers>
        {/* ================= HEPSİ GELDİ ================= */}
        <Stack direction="row" alignItems="center" mb={2}>
          <Checkbox checked={allArrived} onChange={handleAllArrivedChange} />
          <Typography fontWeight={600}>{translate("allArrived")}</Typography>
        </Stack>

        {/* ================= ITEM LIST ================= */}
        <Stack spacing={1.25}>
          {order.orderItems.map((item) => {
            const maxQty = getMaxQty(item);

            // TextField controlled/uncontrolled warning önlemek için:
            const value =
              typeof quantities[item.id] === "number"
                ? quantities[item.id]
                : "";

            // 🔒 maxQty === 0 → her zaman disabled
            const isItemFullyReceived = maxQty === 0;
            const isDisabled = allArrived || isItemFullyReceived;

            return (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  opacity: isDisabled ? 0.6 : 1,
                  transition: "opacity .15s ease",
                }}
              >
                {/* HEADER */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={item.imageUrl}
                    variant="rounded"
                    sx={{ width: 56, height: 56 }}
                  />

                  <Box flex={1}>
                    <Typography fontWeight={600}>{item.productName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.productCode}
                    </Typography>
                  </Box>

                  <Stack spacing={0.25} minWidth={160}>
                    <Typography variant="body2">
                      {translate("shippedQuantity")}: {item.quantityShipped}
                    </Typography>
                    <Typography variant="body2">
                      {translate("receivedQuantity")}: {item.receivedQuantity}
                    </Typography>
                  </Stack>
                </Stack>

                {/* INPUT */}
                <Box mt={2} ml={7}>
                  <TextField
                    label={translate("arrivedQuantity")}
                    size="small"
                    type="number"
                    disabled={isDisabled}
                    value={value}
                    onChange={(e) =>
                      handleQuantityChange(
                        item.id,
                        Number(e.target.value),
                        maxQty,
                      )
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: maxQty,
                        step: 1,
                      },
                    }}
                    sx={{ width: 160 }}
                    helperText={
                      isItemFullyReceived
                        ? translate("itemFullyReceived") // varsa i18n key
                        : `${translateWithoutPrefix("filters.placeholders.max")}: ${maxQty}` // yoksa bunu kaldırabilirsin
                    }
                  />
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={!isDirty || loading}
        >
          {translateWithoutPrefix("buttons.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
